import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Save, Trash2 } from "lucide-react";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";

const frequencies = ["Monthly", "Quarterly", "Twice a year", "Yearly"];

const makeKpi = (sectionNumber, index = 1) => ({
  id: `${sectionNumber}-${index}`,
  text: "",
  maxScore: "",
});

const makeSection = (sectionNumber) => ({
  id: `${sectionNumber}`,
  title: "Performance Title",
  kpis: [makeKpi(sectionNumber, 1)],
});

const numberOnly = (value) => `${value || ""}`.replace(/[^\d]/g, "");

export default function NewEvaluation() {
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [frequency, setFrequency] = useState("Quarterly");
  const [selfAssessmentOpen, setSelfAssessmentOpen] = useState(false);
  const [managerAssessmentOpen, setManagerAssessmentOpen] = useState(false);
  const [sections, setSections] = useState([makeSection(1)]);
  const [remarks, setRemarks] = useState("");
  const [isLoadingMeta, setIsLoadingMeta] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const apiBaseUrl = getApiBaseUrl();
  const navigate = useNavigate();

  const loadDepartments = useCallback(async () => {
    if (!apiBaseUrl) {
      setMessage("API base URL is not configured.");
      setIsLoadingMeta(false);
      return;
    }

    try {
      setIsLoadingMeta(true);
      const response = await axios.get(`${apiBaseUrl}/organizations/departments`);
      setDepartments(response?.data?.departments || []);
      setMessage("");
    } catch (error) {
      const responseMessage = error?.response?.data?.message || "Unable to load departments.";
      setMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsLoadingMeta(false);
    }
  }, [apiBaseUrl]);

  const loadRoles = useCallback(
    async (departmentId) => {
      if (!apiBaseUrl || !departmentId) {
        setRoles([]);
        return;
      }

      try {
        const response = await axios.get(`${apiBaseUrl}/organizations/roles`, {
          params: { departmentId },
        });
        setRoles(response?.data?.roles || []);
      } catch (error) {
        const responseMessage = error?.response?.data?.message || "Unable to load roles.";
        setMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
        setRoles([]);
      }
    },
    [apiBaseUrl],
  );

  useEffect(() => {
    void loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    setSelectedRoleId("");
    void loadRoles(selectedDepartmentId);
  }, [selectedDepartmentId, loadRoles]);

  const selectedDepartment = useMemo(
    () => departments.find((department) => department.id === selectedDepartmentId),
    [departments, selectedDepartmentId],
  );

  const selectedRole = useMemo(
    () => roles.find((role) => role.id === selectedRoleId),
    [roles, selectedRoleId],
  );

  const totalMaximum = useMemo(
    () =>
      sections.reduce(
        (sum, section) =>
          sum + section.kpis.reduce((kpiSum, kpi) => kpiSum + (Number(kpi.maxScore) || 0), 0),
        0,
      ),
    [sections],
  );

  const addKpi = (sectionId) => {
    setSections((current) =>
      current.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          kpis: [...section.kpis, makeKpi(section.id, section.kpis.length + 1)],
        };
      }),
    );
  };

  const removeKpi = (sectionId, kpiId) => {
    setSections((current) =>
      current.map((section) => {
        if (section.id !== sectionId || section.kpis.length === 1) return section;
        return { ...section, kpis: section.kpis.filter((kpi) => kpi.id !== kpiId) };
      }),
    );
  };

  const addSection = () => {
    setSections((current) => [...current, makeSection(current.length + 1)]);
  };

  const updateSection = (sectionId, title) => {
    setSections((current) =>
      current.map((section) => (section.id === sectionId ? { ...section, title } : section)),
    );
  };

  const updateKpi = (sectionId, kpiId, updates) => {
    setSections((current) =>
      current.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          kpis: section.kpis.map((kpi) => (kpi.id === kpiId ? { ...kpi, ...updates } : kpi)),
        };
      }),
    );
  };

  const resetForm = () => {
    setSelectedDepartmentId("");
    setSelectedRoleId("");
    setFrequency("Quarterly");
    setSelfAssessmentOpen(false);
    setManagerAssessmentOpen(false);
    setSections([makeSection(1)]);
    setRemarks("");
    setMessage("");
  };

  const handleSave = async () => {
    if (!selectedDepartmentId || !selectedRoleId) {
      setMessage("Select a department and role before saving.");
      return;
    }

    const hasBlankKpi = sections.some(
      (section) =>
        !section.title.trim() ||
        section.kpis.some((kpi) => !kpi.text.trim() || Number(kpi.maxScore) <= 0),
    );

    if (hasBlankKpi) {
      setMessage("Complete every title, KPI criterion and score point.");
      return;
    }

    if (totalMaximum !== 100) {
      setMessage("The KPI score points must add up to exactly 100.");
      return;
    }

    try {
      setIsSaving(true);
      setMessage("");
      await axios.post(`${apiBaseUrl}/organizations/performance-evaluations`, {
        departmentId: selectedDepartmentId,
        roleId: selectedRoleId,
        frequency,
        selfAssessmentOpen,
        managerAssessmentOpen,
        remarks,
        sections: sections.map((section) => ({
          id: section.id,
          title: section.title.trim(),
          kpis: section.kpis.map((kpi) => ({
            id: kpi.id,
            text: kpi.text.trim(),
            maxScore: Number(kpi.maxScore),
          })),
        })),
      });
      navigate("/performance-evaluation");
    } catch (error) {
      const responseMessage = error?.response?.data?.message || "Unable to save evaluation.";
      setMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-8xl mx-auto p-4 sm:p-6 font-Nunito">
      <div className="mb-6 flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-black">Performance Evaluation</h1>
        <p className="text-sm text-slate-500">Create role-based KPI templates for every matching staff member.</p>
      </div>

      <div className="bg-white p-5 sm:p-6">
        {message ? (
          <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            {message}
          </div>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-4">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-slate-800">Department</span>
            <select
              value={selectedDepartmentId}
              onChange={(event) => setSelectedDepartmentId(event.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900"
            >
              <option value="">{isLoadingMeta ? "Loading departments..." : "Select department"}</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.departmentName || department.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-slate-800">Role</span>
            <select
              value={selectedRoleId}
              onChange={(event) => setSelectedRoleId(event.target.value)}
              disabled={!selectedDepartmentId}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 disabled:bg-slate-50"
            >
              <option value="">{selectedDepartmentId ? "Select role" : "Choose department first"}</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.roleName || role.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-slate-800">Frequency</span>
            <select
              value={frequency}
              onChange={(event) => setFrequency(event.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900"
            >
              {frequencies.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase text-slate-500">Score total</p>
            <p className={`text-2xl font-semibold ${totalMaximum === 100 ? "text-emerald-600" : "text-slate-900"}`}>
              {totalMaximum}/100
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={selfAssessmentOpen}
              onChange={(event) => setSelfAssessmentOpen(event.target.checked)}
              className="h-4 w-4"
            />
            Staff self scoring open
          </label>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={managerAssessmentOpen}
              onChange={(event) => setManagerAssessmentOpen(event.target.checked)}
              className="h-4 w-4"
            />
            Line manager scoring open
          </label>
        </div>

        <div className="mt-6 rounded-lg bg-blue-50 p-5">
          <div className="flex flex-col gap-1 text-sm text-slate-700 sm:flex-row sm:justify-between">
            <span>{selectedDepartment?.departmentName || "Department not selected"}</span>
            <span>{selectedRole?.roleName || "Role not selected"}</span>
          </div>
        </div>

        <div className="mt-6 space-y-5">
          {sections.map((section, sectionIndex) => (
            <div key={section.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex items-center gap-3">
                <span className="text-lg font-semibold text-black">{sectionIndex + 1}.</span>
                <input
                  value={section.title}
                  onChange={(event) => updateSection(section.id, event.target.value)}
                  className="w-full border-0 border-b border-slate-200 px-1 py-2 text-lg font-semibold text-black outline-none focus:border-blue-500"
                />
              </div>

              <div className="mb-3 grid grid-cols-[48px,1fr,260px,40px] gap-3 text-xs font-semibold uppercase text-slate-500 max-lg:hidden">
                <span>No</span>
                <span>Key performance indicator</span>
                <span className="grid grid-cols-5 gap-2 text-center">
                  <span>Self</span>
                  <span>HOD</span>
                  <span>Org</span>
                  <span>Total</span>
                  <span>Max</span>
                </span>
                <span className="sr-only">Remove</span>
              </div>

              <div className="space-y-3">
                {section.kpis.map((kpi, kpiIndex) => {
                  const maxScore = Number(kpi.maxScore) || 0;
                  return (
                    <div key={kpi.id} className="grid gap-3 lg:grid-cols-[48px,1fr,260px,40px] lg:items-center">
                      <span className="text-sm font-medium text-black">{sectionIndex + 1}.{kpiIndex + 1}</span>
                      <input
                        value={kpi.text}
                        onChange={(event) => updateKpi(section.id, kpi.id, { text: event.target.value })}
                        placeholder={`Enter KPI ${sectionIndex + 1}.${kpiIndex + 1}`}
                        className="min-h-[46px] rounded-lg border border-slate-300 px-4 text-sm text-slate-900 outline-none focus:border-blue-500"
                      />
                      <div className="grid grid-cols-5 gap-2">
                        {[maxScore, maxScore, maxScore, maxScore].map((value, boxIndex) => (
                          <input
                            key={boxIndex}
                            value={value || ""}
                            readOnly
                            className="h-10 rounded-md border border-slate-300 bg-slate-50 text-center text-sm text-slate-500"
                          />
                        ))}
                        <input
                          value={kpi.maxScore}
                          inputMode="numeric"
                          onChange={(event) => updateKpi(section.id, kpi.id, { maxScore: numberOnly(event.target.value) })}
                          className="h-10 rounded-md border border-slate-300 text-center text-sm text-slate-900 outline-none focus:border-blue-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeKpi(section.id, kpi.id)}
                        disabled={section.kpis.length === 1}
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-red-500 text-white disabled:cursor-not-allowed disabled:opacity-40"
                        title="Remove KPI"
                        aria-label={`Remove KPI ${sectionIndex + 1}.${kpiIndex + 1}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => addKpi(section.id)}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white"
                  title="Add KPI"
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex justify-center">
          <button
            type="button"
            onClick={addSection}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white"
          >
            <Plus size={18} />
            Add title
          </button>
        </div>

        <label className="mt-6 block">
          <span className="mb-2 block text-sm font-semibold text-slate-800">Remarks</span>
          <textarea
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
            className="min-h-[110px] w-full rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none focus:border-blue-500"
          />
        </label>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={resetForm}
            className="rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || totalMaximum !== 100}
            className="flex items-center justify-center gap-2 rounded-lg bg-[#1234CD] px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Save size={16} />
            {isSaving ? "Saving..." : "Save KPI"}
          </button>
        </div>
      </div>
    </div>
  );
}
